import React, { useMemo, useState } from 'react';

import { FormControl, InputLabel, Typography } from '@mui/material';
import { mergeSlotProps } from '@mui/material/utils';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import dayjs from '@/components/NewUI/utils/dayjs';

import { sharedClasses } from './sharedClasses';
import { userTimeZone } from '../../utils/formatToUserTimeZone';

import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import type {
  DateTimeValidationError,
  DateValidationError,
  DesktopDatePickerProps,
  DesktopDateTimePickerProps,
  PickerChangeHandlerContext,
  StaticDatePickerProps,
  TimeValidationError
} from '@mui/x-date-pickers';
import type { TimePickerProps } from '@mui/x-date-pickers/TimePicker';
import type { Dayjs } from 'dayjs';
import type { CSSProperties, ReactNode } from 'react';

type DateInput = Date | Dayjs | string;

const toPickerDate = (value: { toDate: () => Date } | null): Dayjs | null => {
  if (value === null) return null;
  return dayjs.isDayjs(value) ? value : dayjs(value.toDate());
};

const toPickerValue = (value: DateInput, format?: string): Dayjs => {
  if (typeof value !== 'string') return dayjs(value);
  const datePart = /^\d{4}-\d{2}-\d{2}/.exec(value)?.[0];
  if (datePart && !dayjs(datePart, 'YYYY-MM-DD', true).isValid()) return dayjs(NaN);
  return format ? dayjs(value, format) : dayjs(value);
};

interface IUncontrolledSingleDatePickerProps extends Omit<
  DesktopDatePickerProps,
  'defaultValue' | 'minDate' | 'maxDate'
> {
  defaultValue: DateInput | null;
  handleDateChange: (date: Dayjs | null) => void;
  maxDate?: DateInput;
  minDate?: DateInput;
  inputStyles?: SystemStyleObject<Theme> | CSSProperties;
  disabled?: boolean;
  innerRef?: React.RefObject<HTMLInputElement> | ((el: HTMLDivElement) => void);
  error?: boolean;
}

interface ISingleDatePickerProps extends Omit<
  DesktopDatePickerProps,
  'value' | 'onChange' | 'minDate' | 'maxDate'
> {
  value: DateInput | null;
  onChange: (
    value: Dayjs | null,
    keyboardInputValue?: PickerChangeHandlerContext<DateValidationError>
  ) => void;
  maxDate?: DateInput;
  minDate?: DateInput;
  inputStyles?: SystemStyleObject<Theme> | CSSProperties;
  label?: string | ReactNode;
  showTodayButton?: boolean;
  error?: string;
  required?: boolean;
  innerRef?: React.RefObject<HTMLInputElement> | ((el: HTMLDivElement) => void);
}

const todayButtonStyling = {
  '& .MuiDialogActions-root': {
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: 3,
    '& button': sharedClasses.genericButtonSecondary
  },
  '& .MuiDateCalendar-root': {
    height: '325px'
  }
};

type TDatePickerSlotProps = NonNullable<DesktopDatePickerProps['slotProps']>;

const mergeDatePickerSlotProps = (
  base: TDatePickerSlotProps,
  override?: TDatePickerSlotProps
): TDatePickerSlotProps => {
  const o = override ?? {};
  return {
    ...base,
    ...o,
    popper: mergeSlotProps(o.popper, base.popper ?? {}),
    textField: (ownerState) => {
      const baseField =
        typeof base.textField === 'function' ? base.textField(ownerState) : base.textField;
      const overrideField =
        typeof o.textField === 'function' ? o.textField(ownerState) : o.textField;
      return {
        ...mergeSlotProps(overrideField, baseField ?? {}),
        slotProps: {
          ...baseField?.slotProps,
          ...overrideField?.slotProps,
          input: mergeSlotProps(overrideField?.slotProps?.input, baseField?.slotProps?.input ?? {}),
          htmlInput: {
            ...baseField?.slotProps?.htmlInput,
            ...overrideField?.slotProps?.htmlInput
          }
        }
      };
    },
    actionBar: mergeSlotProps(o.actionBar, base.actionBar ?? {})
  };
};

interface ISingleDatePickerTimeProps extends Omit<
  DesktopDateTimePickerProps,
  'value' | 'onChange' | 'minDate' | 'maxDate'
> {
  value: DateInput | null;
  onChange: (
    value: Dayjs | null,
    context?: PickerChangeHandlerContext<DateTimeValidationError>
  ) => void;
  minDate?: DateInput;
  maxDate?: DateInput;
  inputStyles?: SystemStyleObject<Theme> | CSSProperties;
  pickerStyles?: DesktopDateTimePickerProps['sx'];
}

interface ISingleDateTimePickerProps extends ISingleDatePickerTimeProps {
  disableMaskedInput?: boolean;
  label?: string | ReactNode;
  timezone?: string;
  disablePast?: boolean;
  required?: boolean;
  innerRef?: React.RefObject<HTMLInputElement | null>;
  error?: string;
}

interface IControlledTimePicker extends Omit<TimePickerProps, 'value' | 'onChange'> {
  value: Dayjs | null;
  onChange: (value: Dayjs | null, context: PickerChangeHandlerContext<TimeValidationError>) => void;
  label?: string;
  minutesInterval?: number;
  inputStyles?: SystemStyleObject<Theme> | CSSProperties;
}

interface IControlledStaticTimePicker extends Omit<StaticDatePickerProps, 'value' | 'onChange'> {
  value: DateInput | null;
  onChange: (value: Dayjs | null) => void;
}

export const SingleDatePicker = ({
  value,
  onChange,
  maxDate,
  minDate,
  inputStyles,
  label,
  showTodayButton,
  error,
  required,
  disabled,
  innerRef,
  slotProps: restSlotProps,
  ...pickerRest
}: ISingleDatePickerProps) => {
  const baseSlotProps: TDatePickerSlotProps = {
    popper: {
      sx: { ...sharedClasses.calendar, ...(showTodayButton && todayButtonStyling) },
      placement: 'bottom-end',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [28, 15]
          }
        }
      ]
    },
    textField: {
      sx: {
        width: '100%',
        marginTop: label ? '32px' : '2px',
        '.MuiPickersSectionList-root': {
          padding: '5px 0px 7.5px 0px',
          fontSize: '14px',
          width: 'unset'
        },
        '& fieldset': {
          ...(disabled && { background: 'grey', opacity: 0.2, borderRadius: '6px' })
        }
      },
      slotProps: {
        input: { sx: { ...sharedClasses.datePicker, ...inputStyles } },
        htmlInput: { 'data-testid': 'date-time-picker-input' }
      }
    },
    ...(showTodayButton && { actionBar: { actions: ['today'] } })
  };

  const slotProps = mergeDatePickerSlotProps(baseSlotProps, restSlotProps);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl sx={{ margin: 0, width: '100%' }} variant="standard">
        {label ? (
          <InputLabel shrink htmlFor="text-input" sx={sharedClasses.inputLabel}>
            {label}
            {required ? <span>*</span> : null}
          </InputLabel>
        ) : null}
        <DesktopDatePicker
          ref={innerRef}
          disabled={disabled}
          format="DD/MM/YYYY"
          maxDate={maxDate === undefined ? undefined : toPickerValue(maxDate)}
          minDate={minDate === undefined ? undefined : toPickerValue(minDate)}
          onChange={(next, context) => onChange(toPickerDate(next), context)}
          slotProps={slotProps}
          timezone={userTimeZone}
          value={value === null ? null : toPickerValue(value)}
          {...pickerRest}
        />
        <Typography sx={sharedClasses.errorMessage} variant="caption">
          {error}
        </Typography>
      </FormControl>
    </LocalizationProvider>
  );
};

export const SingleDateTimePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  inputStyles,
  pickerStyles,
  label,
  required,
  innerRef,
  error,
  ...rest
}: ISingleDateTimePickerProps) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <FormControl sx={{ width: '100%', margin: 0 }} variant="standard">
      {label ? (
        <InputLabel shrink htmlFor="text-input" sx={sharedClasses.inputLabel}>
          {label}
          {required ? <span>*</span> : null}
        </InputLabel>
      ) : null}
      <DesktopDateTimePicker
        ref={innerRef}
        format="DD/MM/YYYY hh:mm a"
        maxDate={maxDate === undefined ? undefined : toPickerValue(maxDate)}
        minDate={minDate === undefined ? undefined : toPickerValue(minDate)}
        onChange={(next, context) => onChange(toPickerDate(next), context)}
        openTo="day"
        timeSteps={{ hours: 1, minutes: 1 }}
        value={value === null ? null : toPickerValue(value)}
        views={['year', 'month', 'day', 'hours', 'minutes']}
        slotProps={{
          popper: {
            sx: pickerStyles,
            placement: 'bottom-end',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [28, 15]
                }
              }
            ]
          },
          textField: {
            error: !!error,

            sx: {
              marginTop: label ? '24px' : '0px',
              '.MuiPickersSectionList-root': {
                padding: '8px 0px',
                fontSize: '15px',
                width: '186px'
              },
              '& fieldset': {
                borderRadius: '6px',
                borderColor: '#DDDDDD'
              }
            },

            slotProps: {
              input: {
                sx: {
                  ...sharedClasses.datePicker,
                  ...inputStyles,
                  '&.Mui-error fieldset': {
                    borderColor: '#eca8a6 !important'
                  }
                }
              },

              htmlInput: {
                'data-testid': 'date-time-picker-input'
              }
            }
          },
          actionBar: { actions: ['today', 'cancel', 'accept'] }
        }}
        {...rest}
      />
      {error ? (
        <Typography sx={sharedClasses.errorMessage} variant="caption">
          {error}
        </Typography>
      ) : null}
    </FormControl>
  </LocalizationProvider>
);

export const UncontrolledSingleDatePicker = ({
  defaultValue,
  handleDateChange,
  maxDate,
  minDate,
  inputStyles,
  disabled = false,
  innerRef,
  format = 'DD/MM/YYYY',
  error = false,
  ...rest
}: IUncontrolledSingleDatePickerProps) => {
  const [date, setDate] = useState<Dayjs | null>(() => {
    if (defaultValue === null) return null;
    return toPickerValue(defaultValue, format);
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DesktopDatePicker
        ref={innerRef}
        disabled={disabled}
        format={format}
        maxDate={maxDate === undefined ? undefined : toPickerValue(maxDate)}
        minDate={minDate === undefined ? undefined : toPickerValue(minDate)}
        value={date}
        onChange={(next) => {
          const nextDate = toPickerDate(next);
          setDate(nextDate);
          handleDateChange(nextDate);
        }}
        slotProps={{
          popper: {
            sx: sharedClasses.calendar,
            placement: 'bottom-end',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [28, 15]
                }
              }
            ]
          },
          textField: {
            sx: {
              '.MuiPickersSectionList-root': { padding: '8px 0px' },
              '& fieldset': { borderRadius: '6px' }
            },

            error,

            slotProps: {
              input: { sx: { ...inputStyles } }
            }
          }
        }}
        {...rest}
      />
    </LocalizationProvider>
  );
};

export const ControlledTimePicker = ({
  value,
  onChange,
  label,
  minutesInterval = 1,
  ...rest
}: IControlledTimePicker) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    {label ? (
      <InputLabel shrink htmlFor="text-input" sx={sharedClasses.inputLabel}>
        {label}
      </InputLabel>
    ) : null}
    <DesktopTimePicker
      onChange={(next, context) => onChange(toPickerDate(next), context)}
      timeSteps={{ minutes: minutesInterval }}
      value={value}
      slotProps={{
        popper: { sx: { ...sharedClasses.calendar } },
        textField: {
          sx: sharedClasses.materialTimePicker,

          slotProps: {
            htmlInput: {
              'data-testid': 'time-picker-input'
            }
          }
        }
      }}
      {...rest}
    />
  </LocalizationProvider>
);

export const ControlledStaticDatePicker = ({
  value,
  onChange,
  ...rest
}: IControlledStaticTimePicker) => {
  // MUI's calendar jumps back to the selected day's month whenever the value's reference changes,
  // so a parent re-render after a month change must hand it the same object for the same instant.
  const valueTime = value === null ? null : toPickerValue(value).valueOf();
  const pickerValue = useMemo(() => (valueTime === null ? null : dayjs(valueTime)), [valueTime]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StaticDatePicker
        onChange={(next) => onChange(toPickerDate(next))}
        timezone={userTimeZone}
        value={pickerValue}
        slotProps={{
          calendarHeader: {
            sx: sharedClasses.calendarHeader
          },
          actionBar: {
            actions: []
          }
        }}
        {...rest}
      />
    </LocalizationProvider>
  );
};
